import { Body1, Button, Text, Tooltip } from '@fluentui/react-components';
import { bundleIcon, Edit16Filled, Edit16Regular } from '@fluentui/react-icons';

import {
  DATASET_RETENTION_DEFAULT,
  DATASET_RETENTION_LABELS,
  DATASET_USAGE_RESTRICTION_LABELS,
} from '../../../constants/index.js';
import { DatasetUsageSection } from '../DatasetUsageSection/DatasetUsageSection.js';

import type { DatasetOverviewTabProps } from './DatasetOverviewTab.interface.js';
import { useStyles } from './DatasetOverviewTab.style.js';

const EditIcon = bundleIcon(Edit16Filled, Edit16Regular) as React.FC;

const EDIT_BUTTON_LABEL = 'Edit';

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatUsageRestriction(values?: string[]): string | undefined {
  if (!values || values.length === 0) return undefined;
  return values.map((v) => DATASET_USAGE_RESTRICTION_LABELS[v] ?? v).join(', ');
}

function formatRetention(value?: string): string | undefined {
  if (!value || value === DATASET_RETENTION_DEFAULT) return undefined;
  return DATASET_RETENTION_LABELS[value] ?? value;
}

function formatSourceProperties(
  props?: Record<string, unknown> | null,
): string | undefined {
  if (!props || Object.keys(props).length === 0) return undefined;
  return JSON.stringify(props, null, 2);
}

export function DatasetOverviewTab({
  dataset,
  dataEntryCount,
  canEdit = false,
  editDisabledReason,
  onEditClick,
}: DatasetOverviewTabProps) {
  const styles = useStyles();

  const usageRestriction = formatUsageRestriction(dataset.usageRestriction);
  const retention = formatRetention(dataset.retentionOption);
  const sourceProperties = formatSourceProperties(dataset.sourceProperties);

  const editButton = onEditClick ? (
    canEdit ? (
      <Button
        appearance="secondary"
        size="small"
        icon={<EditIcon />}
        onClick={onEditClick}
      >
        {EDIT_BUTTON_LABEL}
      </Button>
    ) : (
      <Tooltip content={editDisabledReason ?? ''} relationship="description">
        <Button
          appearance="secondary"
          size="small"
          icon={<EditIcon />}
          disabledFocusable
        >
          {EDIT_BUTTON_LABEL}
        </Button>
      </Tooltip>
    )
  ) : null;

  return (
    <div className={styles.root}>
      {editButton !== null && <div className={styles.header}>{editButton}</div>}

      <div className={styles.overviewGrid}>
        <div>
          <Text as="span" className={styles.fieldLabel}>
            Access
          </Text>
          <Text as="span" className={styles.fieldValue}>
            {dataset.accessLevel ?? '—'}
          </Text>
        </div>

        <div>
          <Text as="span" className={styles.fieldLabel}>
            Status
          </Text>
          <Text as="span" className={styles.fieldValue}>
            {dataset.status}
          </Text>
        </div>

        <div>
          <Text as="span" className={styles.fieldLabel}>
            Created
          </Text>
          <Text as="span" className={styles.fieldValue}>
            {formatDate(dataset.createdAt)}
          </Text>
        </div>

        <div>
          <Text as="span" className={styles.fieldLabel}>
            Last Modified
          </Text>
          <Text as="span" className={styles.fieldValue}>
            {formatDate(dataset.updatedAt)}
          </Text>
        </div>

        <div>
          <Text as="span" className={styles.fieldLabel}>
            Owner
          </Text>
          <Text as="span" className={styles.fieldValue}>
            {dataset.creatorName ?? dataset.ownerAlias ?? '—'}
          </Text>
        </div>

        <div>
          <Text as="span" className={styles.fieldLabel}>
            Data Type
          </Text>
          <Text as="span" className={styles.fieldValue}>
            {dataset.dataType ?? '—'}
          </Text>
        </div>

        <div>
          <Text as="span" className={styles.fieldLabel}>
            Data Entries
          </Text>
          <Text as="span" className={styles.fieldValue}>
            {dataEntryCount ?? '—'}
          </Text>
        </div>

        {usageRestriction !== undefined && (
          <div>
            <Text as="span" className={styles.fieldLabel}>
              Usage Restriction
            </Text>
            <Text as="span" className={styles.fieldValue}>
              {usageRestriction}
            </Text>
          </div>
        )}

        {dataset.subType && (
          <div>
            <Text as="span" className={styles.fieldLabel}>
              Sub Type
            </Text>
            <Text as="span" className={styles.fieldValue}>
              {dataset.subType}
            </Text>
          </div>
        )}

        {dataset.source && (
          <div>
            <Text as="span" className={styles.fieldLabel}>
              Source
            </Text>
            <Text as="span" className={styles.fieldValue}>
              {dataset.source}
            </Text>
          </div>
        )}

        {retention !== undefined && (
          <div>
            <Text as="span" className={styles.fieldLabel}>
              Retention
            </Text>
            <Text as="span" className={styles.fieldValue}>
              {retention}
            </Text>
          </div>
        )}

        {dataset.description && (
          <div className={styles.descriptionBlock}>
            <Text as="span" className={styles.fieldLabel}>
              Description
            </Text>
            <Body1 className={styles.descriptionValue}>
              {dataset.description}
            </Body1>
          </div>
        )}

        {sourceProperties !== undefined && (
          <div className={styles.jsonBlock}>
            <Text as="span" className={styles.fieldLabel}>
              Source Properties
            </Text>
            <Text as="span" className={styles.jsonValue}>
              {sourceProperties}
            </Text>
          </div>
        )}
      </div>

      <DatasetUsageSection datasetId={dataset.id} />
    </div>
  );
}
